import { qs } from '../../core/dom';
import type { NavModuleInit } from '../../core/types';

/**
 * Wires the server-rendered usefulness feedback panel in the mobile slide-out
 * (see server/src/templates/main.twig). The panel is SSR'd so it appears
 * instantly with the drawer — no lazy load. We own the interaction here so it
 * works without the GTM-gated usefulness-widget JS: a thumb click reveals the
 * "why?" prompt and records the rating (count endpoint + GA4 event + hive BE
 * tracking); Done submits the free-text reason. The floating widget is hidden on
 * mobile via CSS so it doesn't duplicate.
 */
// Whether the rating has already been recorded this page view — prevents repeated
// clicks from re-incrementing the count. Not persisted, so the panel reappears on
// reload.
let recorded = false;

// Hive tracking state for the two-step protocol (mirrors the original trackBE):
// the thumb call sends `type` and starts a record returning a `continue` id; the
// later feedback call reuses that id so the text is linked to the rating.
let beType: string | null = null;
let beContinue: string | null = null;

const track = (name: string, params: Record<string, unknown>): void => {
  // Same GA4 channel the widget uses — consent-managed downstream.
  document.dispatchEvent(new CustomEvent('ga4.track', { detail: { name, params } }));
};

// GA user_pseudo_id from the `_ga` cookie ("GA1.1.<id>"); the original strips the
// first 6 chars ("GA1.1.") to get the client id.
const getGaId = (): string => {
  const entry = document.cookie
    .split(';')
    .map((c) => c.trim())
    .find((c) => c.startsWith('_ga='));
  return entry ? entry.slice('_ga='.length).substring(6) : '';
};

const getDeviceType = (): string => {
  if (window.matchMedia('(any-hover:none)').matches) {
    return window.matchMedia('(max-width: 915px)').matches ? 'mobile' : 'tablet';
  }
  return 'desktop';
};

// US/staging hosts → their AU equivalents (the hive tracking API lives on the AU
// domain). Mirrors the usefulness widget's getAuUrl.
const AU_HOST_MAP: Record<string, string> = {
  'www.finder.com': 'www.finder.com.au',
  'fus.dev': 'fin.dev',
  'site-fus.qa01.au-se1.gcp.finder.com': 'site-fin.qa01.au-se1.gcp.finder.com',
  'site-fus.qa02.au-se1.gcp.finder.com': 'site-fin.qa02.au-se1.gcp.finder.com',
};

// POST a tracking record to the hive API (mirrors the usefulness widget's
// trackBE). Best-effort: failures must never break the UI.
const trackBE = async (params: {
  usefulness?: 0 | 1;
  feedback?: string;
  type?: string;
}): Promise<void> => {
  const host = window.location.hostname;
  let url = `https://${AU_HOST_MAP[host] ?? host}/api/v1.2/?name=tracking`;
  if (params.type !== undefined) {
    beType = params.type;
    url += `&type=${beType}`;
  } else {
    if (beType) url += `&type=${beType}`;
    if (beContinue) url += `&continue=${beContinue}`;
  }

  const dl = (window as unknown as { dataLayer?: Array<Record<string, unknown>> }).dataLayer?.[0] ?? {};
  const data = new FormData();
  data.append('data[gaid]', getGaId());
  data.append('data[site]', host);
  data.append('data[niche]', String(dl.nicheCategory ?? ''));
  data.append('data[template]', String(dl.WPtemplateName ?? ''));
  data.append('data[url]', window.location.href);
  data.append('data[finder_id]', dl.userId && dl.userId !== 0 ? String(dl.userId) : '');
  data.append('data[device]', getDeviceType());
  Object.entries(params).forEach(([name, value]) => {
    data.append(`data[${name}]`, value == null ? '' : String(value));
  });

  try {
    const res = await fetch(url, { method: 'POST', body: data });
    const json = (await res.json().catch(() => ({}))) as { continue?: string };
    if (json?.continue) beContinue = json.continue;
  } catch {
    // best-effort
  }
};

const resetThumbs = (): void => {
  qs<HTMLElement>('#nav-slideout, .nav-slideout')
    ?.querySelectorAll('.feedback-thumbs-up, .feedback-thumbs-down')
    .forEach((b) => b.classList.remove('active'));
};

const setPromptOpen = (open: boolean): void => {
  // The prompt replaces the thumbs card in place (rather than stacking below).
  const card = qs<HTMLElement>('.gn-feedback__card');
  const prompt = qs<HTMLElement>('.gn-feedback__prompt');
  if (card) card.hidden = open;
  if (prompt) prompt.hidden = !open;
};

const openFeedback = (positive: boolean, btn: HTMLElement): void => {
  resetThumbs();
  btn.classList.add('active');

  // Record the rating once per page view — repeated clicks must not re-increment.
  const postId = (window as unknown as { currentPostId?: string }).currentPostId;
  if (!recorded) {
    recorded = true;
    if (positive && postId) {
      fetch(`${window.location.origin}/wp-json/usefulness/v1/count-update?postId=${postId}`).catch(
        () => undefined,
      );
    }
    track('feedbackSubmitted', {
      usefulness: positive ? 'thumbs up' : 'thumbs down',
      location: 'flyout',
    });
    // Hive BE record (matches the original floating widget: type 'comparison').
    void trackBE({ usefulness: positive ? 1 : 0, type: 'comparison' });
  }

  setPromptOpen(true);
};

// Remove the whole feedback panel once feedback is complete (this page view).
const hidePanel = (): void => {
  const panel = qs<HTMLElement>('.nav-slideout__feedback');
  if (panel) panel.hidden = true;
};

const onDrawerClick = (e: Event): void => {
  const target = e.target as HTMLElement;
  const thumb = target.closest<HTMLElement>('.feedback-thumbs-up, .feedback-thumbs-down');
  if (thumb) {
    e.preventDefault();
    e.stopPropagation();
    openFeedback(thumb.classList.contains('feedback-thumbs-up'), thumb);
    return;
  }
  if (target.closest('.gn-feedback__done')) {
    e.preventDefault();
    e.stopPropagation();
    // Capture the free-text reason so it isn't silently discarded.
    const input = qs<HTMLInputElement>('.gn-feedback__input');
    const feedback = input?.value.trim() ?? '';
    track('formSubmitted', { formName: 'usefulness-widget-feedback', feedback });
    // Append the text to this session's hive record (only if a rating was made
    // this view, so there's a `continue` record to link it to).
    if (recorded) void trackBE({ feedback });
    hidePanel();
  }
};

const init: NavModuleInit = () => {
  const drawer = qs<HTMLElement>('#nav-slideout, .nav-slideout');
  if (!drawer) return;
  // Delegated so it covers the SSR'd thumbs + Done regardless of timing.
  drawer.addEventListener('click', onDrawerClick, true);
  return {
    destroy: () => drawer.removeEventListener('click', onDrawerClick, true),
  };
};

export default init;
